import type { CartItem } from '@/utils/types/cart';
import type { ResolvedBranchProduct } from '~/entities/product/@x/order';

/**
 * Перенос ГОСТЕВОЙ корзины на карточки другого филиала.
 *
 * У авторизованного позиции переотправляются на сервер, и он сам подменяет
 * карточку на «двойника» того же блюда (dish_groups). Гостевая корзина живёт
 * в localStorage, сервера в этом пути нет — поэтому раньше у позиции менялся
 * только `branch_id`, а id оставался от прошлого филиала: блюдо есть в меню,
 * но корзина показывала «Товар недоступен», и человек был вынужден удалить
 * позицию и добавить её заново.
 *
 * Здесь та же подмена делается на клиенте по ответу resolve-for-branch.
 */

/** Позиция комбо переносится как есть: у неё нет карточки товара, только слоты */
function isCombo(item: CartItem): boolean {
  return (item as any).itemType === 'combo' || (item as any).combo_id !== undefined;
}

/** Есть ли у позиции выбранные добавки/отбавки — от этого зависит судьба цены */
function hasModifiers(item: CartItem): boolean {
  const withMods = item as { addons?: unknown[]; excludes?: unknown[] };
  return (withMods.addons?.length ?? 0) > 0 || (withMods.excludes?.length ?? 0) > 0;
}

/** Ключ склейки одинаковых позиций после подмены карточек */
function mergeKey(item: CartItem): string {
  const withMods = item as { addons?: number[]; excludes?: number[]; size?: string };
  if (isCombo(item)) return `combo:${String(item.id)}`;
  return [
    String(item.id),
    [...(withMods.addons ?? [])].sort((a, b) => a - b).join('.'),
    [...(withMods.excludes ?? [])].sort((a, b) => a - b).join('.'),
    withMods.size ?? '',
  ].join('|');
}

/**
 * Комбо филиала в объёме, достаточном для переноса: какие слоты у комбо есть
 * в ЭТОМ филиале и какие карточки в них лежат. Сервер отдаёт состав уже
 * отфильтрованным по филиалу — на филиале Б в слоте лежат карточки филиала Б.
 */
export interface BranchComboSnapshot {
  id: string | number;
  groups: Array<{ id: string | number; products: Array<{ id: string | number }> }>;
}

/** Слоты комбо этого филиала: id группы → карточки, которые в ней лежат */
function comboGroupMembers(combo: BranchComboSnapshot): Map<string, Set<string>> {
  const members = new Map<string, Set<string>>();
  for (const group of combo.groups ?? []) {
    members.set(String(group.id), new Set((group.products ?? []).map((p) => String(p.id))));
  }
  return members;
}

/**
 * Слоты, которые после переноса остались с карточкой, недопустимой в этом филиале:
 * блюдо в наборе есть в другом филиале, а здесь его нет. Комбо при этом продаётся —
 * заказать его можно, заменив блюдо, поэтому «недоступным» целиком не метим.
 */
function slotsOutsideBranch(selections: any[], members: Map<string, Set<string>>): boolean {
  return (selections ?? []).some((selection: any) => {
    const allowed = members.get(String(selection.group_id));
    if (!allowed) return false; // такой группы в филиале нет — про неё сказать нечего
    return (selection.product_ids ?? []).some((id: string | number) => !allowed.has(String(id)));
  });
}

/**
 * Перенос состава комбо. Осторожность выше, чем у обычной позиции (как на сервере):
 * карточку слота подменяем ТОЛЬКО если её аналог сам лежит в ТОЙ ЖЕ группе этого
 * комбо в новом филиале — иначе валидатор состава отбил бы подмену на оформлении.
 * Модификаторы следуют за своей карточкой: если родитель остался прежним, добавки
 * не трогаем, иначе вышел бы товар одного филиала с добавками другого.
 */
function remapComboSelections(
  selections: any[],
  byRequested: Map<string, ResolvedBranchProduct>,
  members: Map<string, Set<string>>,
): { selections: any[]; changed: boolean } {
  let changed = false;

  const next = (selections ?? []).map((selection: any) => {
    const groupKey = String(selection.group_id);
    const productMap = new Map<string, string>();

    const productIds = (selection.product_ids ?? []).map((rawId: string | number) => {
      const id = String(rawId);
      const analog = byRequested.get(id)?.product_id;
      if (!analog || analog === id || !members.get(groupKey)?.has(analog)) {
        productMap.set(id, id);
        return rawId;
      }
      productMap.set(id, analog);
      changed = true;
      return typeof rawId === 'number' ? Number(analog) : analog;
    });

    const modifiers = (selection.modifiers ?? []).map((modifier: any) => {
      const parentId = String(modifier.product_id);
      const parentFinal = productMap.get(parentId) ?? parentId;
      if (parentFinal === parentId) return modifier;
      const remapIds = (ids: Array<string | number> | undefined) =>
        (ids ?? []).map((raw) => {
          const analog = byRequested.get(String(raw))?.product_id;
          return analog ? (typeof raw === 'number' ? Number(analog) : analog) : raw;
        });
      return {
        ...modifier,
        product_id: typeof modifier.product_id === 'number' ? Number(parentFinal) : parentFinal,
        addons: remapIds(modifier.addons),
        excludes: remapIds(modifier.excludes),
      };
    });

    const patched: any = { ...selection, product_ids: productIds };
    if (selection.modifiers) patched.modifiers = modifiers;
    return patched;
  });

  return { selections: next, changed };
}


export interface GuestMigrationResult {
  items: CartItem[];
  /** Сколько позиций реально переехало на карточку нового филиала */
  remapped: number;
  /** Позиции, для которых блюда в новом филиале не нашлось (останутся недоступными) */
  unresolved: CartItem[];
  /** Комбо, которых в новом филиале нет вовсе — помечены недоступными */
  unavailableCombos: CartItem[];
  /** Комбо, которое здесь продаётся, но слот остался с блюдом, которого в филиале нет */
  combosNeedingSlotChange: CartItem[];
}

/**
 * Применяет резолв к позициям гостевой корзины.
 *
 * Политика та же, что у остальных потребителей резолва: подмена — это улучшение.
 * Нерезолвленная позиция НЕ удаляется и не молчит — она остаётся в корзине и
 * честно подсветится недоступной (решение удалить принимает человек).
 */
export function remapGuestItemsToBranch(
  items: CartItem[],
  resolved: ReadonlyArray<ResolvedBranchProduct>,
  toBranchId: number,
  /** Комбо нового филиала. undefined — список не удалось получить: комбо не трогаем вовсе */
  branchCombos?: ReadonlyArray<BranchComboSnapshot>,
): GuestMigrationResult {
  const byRequested = new Map(resolved.map((entry) => [entry.requested, entry]));
  const combosById = branchCombos
    ? new Map(branchCombos.map((combo) => [String(combo.id), combo]))
    : null;
  const unresolved: CartItem[] = [];
  const unavailableCombos: CartItem[] = [];
  const combosNeedingSlotChange: CartItem[] = [];
  let remapped = 0;

  const migrated = items.map((item) => {
    const next = { ...item, branch_id: toBranchId } as CartItem;

    if (isCombo(item)) {
      // Список комбо филиала не получен — ведём себя как раньше и ничего не выдумываем
      if (!combosById) return next;

      const comboId = String((item as any).comboId ?? (item as any).combo_id ?? '');
      const branchCombo = combosById.get(comboId);
      if (!branchCombo) {
        // Комбо в новом филиале нет вовсе: заказать его здесь нельзя. Не удаляем
        // молча — помечаем недоступным, решение принимает человек.
        (next as any).stop_list = true;
        unavailableCombos.push(next);
        return next;
      }

      (next as any).stop_list = false;
      const selectionsKey = (item as any).comboSelections === undefined ? 'combo_selections' : 'comboSelections';
      const currentSelections = (item as any)[selectionsKey];
      if (!Array.isArray(currentSelections)) return next;

      const members = comboGroupMembers(branchCombo);
      const result = remapComboSelections(currentSelections, byRequested, members);
      if (result.changed) {
        (next as any)[selectionsKey] = result.selections;
        remapped += 1;
      }

      // Набор здесь продаётся, но блюдо слота в этом филиале не готовят: заказ
      // отклонит его на оформлении, поэтому просим заменить блюдо заранее.
      const finalSelections = (next as any)[selectionsKey] ?? currentSelections;
      if (slotsOutsideBranch(finalSelections, members)) {
        (next as any).combo_slot_unavailable = true;
        combosNeedingSlotChange.push(next);
      } else {
        (next as any).combo_slot_unavailable = false;
      }

      return next;
    }

    const match = byRequested.get(String(item.id));
    if (!match?.product_id) {
      // Блюда в новом филиале нет (или резолв не ответил) — оставляем как есть
      unresolved.push(next);
      return next;
    }

    if (match.product_id === String(item.id)) return next; // карточка уже своя

    const patched = next as CartItem & { productId?: string };
    patched.id = match.product_id;
    if (patched.productId !== undefined) patched.productId = match.product_id;
    if (match.name) patched.name = match.name;
    patched.stop_list = match.stop_list;

    // Цену берём из нового филиала только у позиции без добавок: `price` хранит
    // стоимость позиции целиком, и подмена затёрла бы стоимость добавок.
    // Итоговую сумму всё равно считает сервер при оформлении.
    if (match.price !== null && !hasModifiers(item)) patched.price = match.price;

    remapped += 1;
    return patched;
  });

  // Две карточки одного блюда могли схлопнуться в одну — складываем количество
  const byKey = new Map<string, CartItem>();
  for (const item of migrated) {
    const key = mergeKey(item);
    const existing = byKey.get(key);
    if (existing) existing.quantity += item.quantity;
    else byKey.set(key, item);
  }

  return { items: [...byKey.values()], remapped, unresolved, unavailableCombos, combosNeedingSlotChange };
}
